import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { categories, getCompoundsByCategory, getCategory } from '../data/compounds';
import CompoundCard from '../components/CompoundCard';
import ErrorBoundary from '../components/ErrorBoundary';
import './CategoryDetail.css';

export default function CategoryDetail() {
  const { categoryId } = useParams();
  const category = getCategory(categoryId);
  const [sortBy, setSortBy] = useState('benefit');
  const [filterStatus, setFilterStatus] = useState('all');

  if (!category) {
    return <Navigate to="/categories" replace />;
  }

  const allCompounds = getCompoundsByCategory(categoryId);

  // Filter
  const filtered = allCompounds.filter(c => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'unscheduled') return c.schedulingStatus === 'unscheduled';
    if (filterStatus === 'scheduled') return c.schedulingStatus !== 'unscheduled';
    return true;
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'benefit') return b.benefitScore - a.benefitScore;
    if (sortBy === 'harm') return a.harmScore - b.harmScore;
    if (sortBy === 'net') return (b.benefitScore - b.harmScore) - (a.benefitScore - a.harmScore);
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    return 0;
  });

  // Stats
  const avgBenefit = allCompounds.length
    ? Math.round(allCompounds.reduce((s, c) => s + c.benefitScore, 0) / allCompounds.length)
    : 0;
  const avgHarm = allCompounds.length
    ? Math.round(allCompounds.reduce((s, c) => s + c.harmScore, 0) / allCompounds.length)
    : 0;
  const highEvidence = allCompounds.filter(c => c.evidenceConfidence === 'HIGH').length;
  const unscheduledCount = allCompounds.filter(c => c.schedulingStatus === 'unscheduled').length;
  const topCompound = [...allCompounds].sort((a, b) => b.benefitScore - a.benefitScore)[0];

  return (
    <div
      className="page-content category-detail"
      style={{
        '--cat-color': category.color,
        '--cat-color-light': category.colorLight,
        '--cat-border': category.colorBorder,
      }}
    >
      {/* Hero */}
      <div className="cd-hero">
        <div className="cd-hero-accent" />
        <div className="container">
          <nav className="cd-breadcrumb">
            <Link to="/">Home</Link>
            <span className="bc-sep">›</span>
            <Link to="/categories">Categories</Link>
            <span className="bc-sep">›</span>
            <span>{category.name}</span>
          </nav>

          <div className="cd-hero-content">
            <div className="cd-hero-left">
              <div className="cd-icon-badge">
                <span className="cd-icon">{category.icon}</span>
              </div>
              <div>
                <div className="cd-meta-row">
                  <span className="cd-compound-count-badge">{allCompounds.length} Compounds</span>
                  {unscheduledCount === allCompounds.length && (
                    <span className="badge badge-green">All Unscheduled</span>
                  )}
                </div>
                <h1 className="cd-title">{category.name}</h1>
                <p className="cd-description">{category.description}</p>
              </div>
            </div>

            <div className="cd-hero-stats">
              <div className="cd-stat-card">
                <span className="cd-stat-value benefit">{avgBenefit}</span>
                <span className="cd-stat-label">Avg Benefit Score</span>
              </div>
              <div className="cd-stat-card">
                <span className="cd-stat-value harm">{avgHarm}</span>
                <span className="cd-stat-label">Avg Risk Score</span>
              </div>
              <div className="cd-stat-card">
                <span className="cd-stat-value">{highEvidence}</span>
                <span className="cd-stat-label">High Evidence</span>
              </div>
              <div className="cd-stat-card">
                <span className="cd-stat-value unscheduled">{unscheduledCount}</span>
                <span className="cd-stat-label">Unscheduled</span>
              </div>
            </div>
          </div>

          {/* Top Compound Spotlight */}
          {topCompound && (
            <div className="cd-spotlight">
              <span className="spotlight-label">🏆 Top Compound in Category</span>
              <Link to={`/compounds/${topCompound.id}`} className="spotlight-link">
                {topCompound.name}
                <span className="spotlight-meta">
                  — Benefit Score: <strong>{topCompound.benefitScore}</strong>
                </span>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Controls + Grid */}
      <div className="cd-body">
        <div className="container">
          <div className="cd-controls">
            <div className="cd-controls-left">
              <span className="cd-results-count">
                {sorted.length} of {allCompounds.length} compounds
              </span>
            </div>
            <div className="cd-controls-right">
              <select
                className="cd-select"
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="unscheduled">Unscheduled Only</option>
                <option value="scheduled">Scheduled</option>
              </select>
              <select
                className="cd-select"
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
              >
                <option value="benefit">Sort: Highest Benefit</option>
                <option value="harm">Sort: Lowest Risk</option>
                <option value="net">Sort: Best Net Value</option>
                <option value="name">Sort: Name A–Z</option>
              </select>
            </div>
          </div>

          {sorted.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🔬</div>
              <h3>No compounds match this filter</h3>
              <p>Try adjusting the filter above.</p>
              <button className="btn btn-secondary" style={{ marginTop: 16 }} onClick={() => setFilterStatus('all')}>
                Clear Filter
              </button>
            </div>
          ) : (
            <div className="cd-grid">
              {sorted.map(compound => (
                <ErrorBoundary key={compound.id} compact>
                  <CompoundCard compound={compound} showCategory={false} />
                </ErrorBoundary>
              ))}
            </div>
          )}

          {/* Related Categories */}
          <div className="cd-related">
            <h2 className="cd-related-title">Other Research Categories</h2>
            <div className="cd-related-grid">
              {categories
                .filter(c => c.id !== category.id)
                .map(cat => {
                  const count = getCompoundsByCategory(cat.id).length;
                  return (
                    <Link
                      key={cat.id}
                      to={`/categories/${cat.id}`}
                      className="cd-related-card"
                      style={{ '--cat-color': cat.color }}
                    >
                      <span className="cd-related-icon">{cat.icon}</span>
                      <div className="cd-related-info">
                        <span className="cd-related-name">{cat.name}</span>
                        <span className="cd-related-count">{count} compound{count !== 1 ? 's' : ''}</span>
                      </div>
                      <span className="cd-related-arrow">→</span>
                    </Link>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}